import React, { useState } from "react";
import { Box, Paper, Typography, Tabs, Tab, Badge } from "@mui/material";
import { format, parse, isSameDay, isWithinInterval, addDays, startOfDay, endOfDay } from "date-fns";

import { ActivityBookingDetails } from "../model/ActivityBookingDetails";
import ActivityBookingsList from "./getActivityBookings";
import { useTheme } from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
    <div role="tabpanel" hidden={value !== index}>
        {value === index && (
            <Box sx={{ pt: { xs: 0.5, sm: 0 }, height: "100%" }}>
                {children}
            </Box>
        )}
    </div>
);

const OperationalDashboard: React.FC<{ darkMode: boolean; rows: ActivityBookingDetails[] }> = ({ rows }) => {
    const [tabIndex, setTabIndex] = useState(0);
    const [selectedDay, setSelectedDay] = useState<Date>(new Date()); 

    const theme = useTheme();
    const isDarkMode = theme.palette.mode === 'dark';

    const today = startOfDay(new Date());
    const tomorrow = addDays(today, 1);
    const weekEnd = endOfDay(addDays(today, 7));

    // Conteggi per badge (Logiche originali)
    const countToday = rows.filter(b => b.StartDateTime && isSameDay(new Date(b.StartDateTime), today)).length;
    const countTomorrow = rows.filter(b => b.StartDateTime && isSameDay(new Date(b.StartDateTime), tomorrow)).length;
    const countWeek = rows.filter(b =>
        b.StartDateTime && isWithinInterval(new Date(b.StartDateTime), { start: today, end: weekEnd })
    ).length;
    
    const countSelected = rows.filter(b =>
        b.StartDateTime && isSameDay(new Date(b.StartDateTime), selectedDay)
    ).length;
    const displayedDay = tabIndex === 1 ? tomorrow : tabIndex === 3 ? selectedDay : today;
    const displayedDate = tabIndex === 2
        ? `${format(today, "dd/MM/yyyy")} – ${format(weekEnd, "dd/MM/yyyy")}`
        : format(displayedDay, "dd/MM/yyyy");
    const displayedCount = [countToday, countTomorrow, countWeek, countSelected][tabIndex];

    return (
        <Box sx={{ width: "100%", minWidth: 0, p: { xs: 0, sm: 2 } }}>
            <Paper elevation={2} sx={{ display: 'flex', flexDirection: 'column', borderRadius: 2, overflow: 'hidden' }}>
                
                {/* Header compatto con Titolo e Tabs integrati */}
                <Box sx={{ 
                    px: { xs: 1, sm: 2 }, pt: 1, pb: 0,
                    display: 'flex', 
                    flexDirection: { xs: 'column', md: 'row' }, 
                    alignItems: { xs: 'flex-start', md: 'center' },
                    justifyContent: 'space-between',
                    gap: 1,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    bgcolor: "background.paper"
                }}>
                    <Typography variant="h6" sx={{ fontWeight: "bold", py: 1, whiteSpace: 'nowrap', flexShrink: 0 }}>
                        Dashboard Operativa
                    </Typography>
                    {/* Contenitore Tabs allineato a destra */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, flexWrap: { xs: 'wrap', md: 'nowrap' }, width: { xs: '100%', md: 'auto' }, flexGrow: 1, minWidth: 0, ml: 'auto' }}>
                        <Tabs
                            value={tabIndex <= 2 ? tabIndex : false}
                            onChange={(_, newIndex) => setTabIndex(newIndex)}
                            variant="scrollable"
                            scrollButtons="auto"
                            aria-label="Dashboard Tabs"
                            sx={{
                                minHeight: 48,
                                flexGrow: 0,
                                flexShrink: 1,
                                minWidth: 0,
                                maxWidth: '100%',
                                ml: 'auto',
                                "& .MuiTabs-indicator": { backgroundColor: "#80CC00" },
                                "& .MuiTab-root": {
                                    minHeight: 48,
                                    textTransform: "none",
                                    fontWeight: 600,
                                    whiteSpace: 'nowrap',
                                    minWidth: { xs: 88, sm: 120 },
                                    px: { xs: 1, sm: 2 },
                                    "&.Mui-selected": { color: "#80CC00" },
                                },
                            }}
                        >
                            <Tab label={<Badge color="primary" badgeContent={countToday}>Oggi</Badge>} />
                            <Tab label={<Badge color="primary" badgeContent={countTomorrow}>Domani</Badge>} />
                            <Tab label={<Badge color="primary" badgeContent={countWeek}>Questa settimana</Badge>} />
                        </Tabs>

                        <Box
                            sx={{
                                minHeight: 48,
                                px: 2,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                position: 'relative',
                                borderBottom: 2,
                                borderColor: tabIndex === 3 ? '#80CC00' : 'transparent',
                                color: tabIndex === 3 ? '#80CC00' : 'text.primary',
                                cursor: 'pointer',
                                flexShrink: 0
                            }}
                        >
                            <Badge color="secondary" badgeContent={displayedCount}>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#80CC00' }}>
                                    {displayedDate}
                                </Typography>
                            </Badge>
                            <CalendarMonthIcon fontSize="small" sx={{ color: '#80CC00' }} />
                            <input
                                type="date"
                                aria-label="Seleziona data delle prenotazioni"
                                value={format(displayedDay, "yyyy-MM-dd")}
                                onChange={(e) => {
                                    if (e.target.value) {
                                        setSelectedDay(parse(e.target.value, "yyyy-MM-dd", new Date()));
                                        setTabIndex(3);
                                    }
                                }}
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    opacity: 0,
                                    cursor: 'pointer',
                                    width: '100%',
                                    height: '100%'
                                }}
                            />
                        </Box>
                    </Box>
                </Box>

                {/* Sezione Liste (TabPanel) */}
                <Box sx={{ p: 0 }}>
                    {/* Oggi */}
                    <TabPanel value={tabIndex} index={0}>
                        <ActivityBookingsList
                            darkMode={isDarkMode}
                            dateFilter={(b: ActivityBookingDetails) =>
                                b.StartDateTime ? isSameDay(new Date(b.StartDateTime), today) : false
                            }
                        />
                    </TabPanel>
                    {/* Domani */}
                    <TabPanel value={tabIndex} index={1}>
                        <ActivityBookingsList
                            darkMode={isDarkMode}
                            dateFilter={(b: ActivityBookingDetails) =>
                                b.StartDateTime ? isSameDay(new Date(b.StartDateTime), tomorrow) : false
                            }
                        />
                    </TabPanel>
                    {/* Questa settimana */}
                    <TabPanel value={tabIndex} index={2}>
                        <ActivityBookingsList
                            darkMode={isDarkMode}
                            dateFilter={(b: ActivityBookingDetails) => {
                                if (!b.StartDateTime) return false;
                                return isWithinInterval(new Date(b.StartDateTime), { start: today, end: weekEnd });
                            }}
                        />
                    </TabPanel>
                    {/* Seleziona data */}
                    <TabPanel value={tabIndex} index={3}>
                        <ActivityBookingsList
                            darkMode={isDarkMode}
                            dateFilter={(b: ActivityBookingDetails) => {
                                return b.StartDateTime ? isSameDay(new Date(b.StartDateTime), selectedDay) : false;
                            }}
                        />
                    </TabPanel>
                </Box>
            </Paper>
        </Box>
    );
};

export default OperationalDashboard;
